import {
  ColumnMapping,
  convertExcelDate,
  ExcelProcessorConfig,
  HeaderConfig,
  ParsedExcelData,
  ValidationError
} from '@/lib/excel';

const HEADERS = {
  MA_VT: 'Mã vật tư*',
  TEN_VT: 'Tên vật tư',
  DVT: 'Đơn vị tính*',
  NGAY_HL: 'Ngày hiệu lực*',
  MA_KHACH_HANG: 'Mã khách hàng',
  TEN_KHACH_HANG: 'Tên khách hàng',
  NHOM_KHACH_HANG_1: 'Nhóm khách hàng 1',
  NHOM_KHACH_HANG_2: 'Nhóm khách hàng 2',
  NHOM_KHACH_HANG_3: 'Nhóm khách hàng 3',
  NGOAI_TE: 'Ngoại tệ*',
  GIA_BAN: 'Giá bán',
  GIA_DA_BAO_GOM_THUE: 'Giá đã bao gồm thuế'
};

const HEADER_CONFIG: HeaderConfig[] = [
  { header: HEADERS.MA_VT, key: 'ma_vt', type: 'main' },
  { header: HEADERS.TEN_VT, key: 'ten_vt', type: 'main' },
  { header: HEADERS.DVT, key: 'dvt', type: 'main' },
  { header: HEADERS.NGAY_HL, key: 'ngay_hl', type: 'main' },
  { header: HEADERS.MA_KHACH_HANG, key: 'ma_kh', type: 'main' },
  { header: HEADERS.TEN_KHACH_HANG, key: 'ten_kh', type: 'main' },
  { header: HEADERS.NHOM_KHACH_HANG_1, key: 'nh_kh1', type: 'main' },
  { header: HEADERS.NHOM_KHACH_HANG_2, key: 'nh_kh2', type: 'main' },
  { header: HEADERS.NHOM_KHACH_HANG_3, key: 'nh_kh3', type: 'main' },
  { header: HEADERS.NGOAI_TE, key: 'ma_nt', type: 'main' },
  { header: HEADERS.GIA_BAN, key: 'gia_nt2', type: 'main' },
  { header: HEADERS.GIA_DA_BAO_GOM_THUE, key: 'thue_yn', type: 'main' }
];

const UUID_FIELDS = {
  main: ['ma_vt', 'dvt', 'ma_kh', 'ma_nt', 'nh_kh1', 'nh_kh2', 'nh_kh3']
};

type UUIDLookupResponse = Record<string, Record<string, Array<string | null | undefined>> | undefined>;

const getFieldName = (key: string) => {
  const config = HEADER_CONFIG.find(c => c.key === key);
  return config?.header?.replace('*', '').trim() || key;
};

const parseNum = (val: unknown, def = 0) => {
  if (val == null || String(val).trim() === '') return def;
  const n = parseFloat(String(val));
  return Number.isNaN(n) ? def : Math.round(n * 100) / 100;
};

const getVal = (
  uuid: UUIDLookupResponse | undefined,
  parsed: Record<string, unknown[]>,
  section: string,
  field: string,
  i: number
) => (uuid?.[section]?.[field]?.[i] || parsed[field]?.[i] || undefined) as string | undefined;

const UUIDPayload = (data: ParsedExcelData) => {
  const build = (section: Record<string, unknown[]>, fields: string[]) =>
    Object.fromEntries(fields.map(f => [f, section[f] || []]));

  return {
    main: build(data.main || {}, UUID_FIELDS.main)
  };
};

const ValidateUUID = (
  response: UUIDLookupResponse | null | undefined,
  columnMapping: Record<string, ColumnMapping>
): ValidationError[] => {
  if (!response) return [];
  const errors: ValidationError[] = [];

  const data = response.main;
  if (!data) return errors;

  Object.entries(data).forEach(([field, values]) => {
    if (!Array.isArray(values)) return;
    const mapping = Object.values(columnMapping).find(m => m.key === field && m.type === 'main');

    values.forEach((val, i) => {
      if (val === null) {
        errors.push({
          row: i + 2,
          field,
          message: `${getFieldName(field)} không hợp lệ`,
          section: 'main',
          columnIndex: mapping?.index
        });
      }
    });
  });

  return errors;
};

const TransformToBodies = (
  dataRows: unknown[][],
  parsedData: ParsedExcelData,
  uuidResponse: UUIDLookupResponse | undefined,
  _context: unknown
): ImportBody[] => {
  const { main = {} } = parsedData;
  const bodies: ImportBody[] = [];

  for (let i = 0; i < dataRows.length; i++) {
    const ma_vt = getVal(uuidResponse, main, 'main', 'ma_vt', i);
    const dvt = getVal(uuidResponse, main, 'main', 'dvt', i);
    const ngay_hl = convertExcelDate(main.ngay_hl?.[i]);
    const ma_nt = getVal(uuidResponse, main, 'main', 'ma_nt', i);
    const ma_kh = getVal(uuidResponse, main, 'main', 'ma_kh', i);
    const nh_kh1 = getVal(uuidResponse, main, 'main', 'nh_kh1', i);
    const nh_kh2 = getVal(uuidResponse, main, 'main', 'nh_kh2', i);
    const nh_kh3 = getVal(uuidResponse, main, 'main', 'nh_kh3', i);
    const gia_nt2 = parseNum(main.gia_nt2?.[i], 0);

    // Parse thue_yn (case insensitive, check for 'x', 'có', 'yes', 'true', '1')
    const rawThue = String(main.thue_yn?.[i] || '')
      .trim()
      .toLowerCase();
    const thue_yn = ['x', 'có', 'yes', 'true', '1'].includes(rawThue);

    // Skip rows without required fields
    if (!ma_vt || !dvt || !ma_nt || !ngay_hl) {
      continue;
    }

    bodies.push({
      ma_vt,
      dvt,
      ngay_hl,
      ma_nt,
      ma_kh: ma_kh || undefined,
      nh_kh1: nh_kh1 || undefined,
      nh_kh2: nh_kh2 || undefined,
      nh_kh3: nh_kh3 || undefined,
      gia_nt2,
      thue_yn,
      status: 1
    });
  }

  return bodies;
};

export interface ImportBody {
  ma_vt: string;
  dvt: string;
  ngay_hl: string;
  ma_nt: string;
  ma_kh?: string;
  nh_kh1?: string;
  nh_kh2?: string;
  nh_kh3?: string;
  gia_nt2: number;
  thue_yn: boolean;
  status: number;
}

export const ExcelConfig: ExcelProcessorConfig<ImportBody> = {
  Headers: HEADERS,
  HeaderConfig: HEADER_CONFIG,
  RequiredFields: [
    { key: 'ma_vt', type: 'main', label: 'Mã vật tư' },
    { key: 'dvt', type: 'main', label: 'Đơn vị tính' },
    { key: 'ngay_hl', type: 'main', label: 'Ngày hiệu lực' },
    { key: 'ma_nt', type: 'main', label: 'Ngoại tệ' }
  ],
  Transform: TransformToBodies,
  UUIDPayload,
  ValidateUUID
};
